/**
 * Messages from managers (broadcasts): who a message is for, the inbox, and
 * read receipts. Pure functions; the screens and the server (0036
 * private.broadcast_is_for) use the same rules.
 */
import type { BroadcastAudience, BroadcastReadRow, BroadcastRow } from '@routeback/core';

export const TITLE_MAX = 80;
export const BODY_MAX = 1000;

/** Is this message for me? Everyone · picked people · people at those terminals. */
export function isForMe(a: BroadcastAudience | null | undefined, userId: string, myTerminals: readonly string[]): boolean {
  if (!a) return false;
  if (a.all) return true;
  if (a.user_ids?.includes(userId)) return true;
  return !!a.terminal_ids?.some((t) => myTerminals.includes(t));
}

export interface InboxItem { b: BroadcastRow; read: boolean }

/** My messages, newest first, with whether I've opened each. Messages I sent aren't in my inbox. */
export function inbox(broadcasts: BroadcastRow[], reads: BroadcastReadRow[], userId: string, myTerminals: readonly string[]): InboxItem[] {
  const opened = new Set(reads.filter((r) => !r.deleted_at && r.user_id === userId).map((r) => r.broadcast_id));
  return broadcasts
    .filter((b) => !b.deleted_at && b.sent_at && b.created_by !== userId && isForMe(b.audience, userId, myTerminals))
    .sort((x, y) => (y.sent_at ?? '').localeCompare(x.sent_at ?? ''))
    .map((b) => ({ b, read: opened.has(b.id) }));
}

/** "Everyone" · "Dan D., Sue K." · "3 people" · "Wheaton terminal". */
export function audienceLabel(a: BroadcastAudience, names: Map<string, string>, terminals: Map<string, string>): string {
  if (a.all) return 'Everyone';
  if (a.terminal_ids?.length) return a.terminal_ids.map((t) => terminals.get(t) ?? 'a terminal').join(', ');
  const ids = a.user_ids ?? [];
  if (ids.length > 3) return `${ids.length} people`;
  return ids.map((u) => names.get(u) ?? 'someone').join(', ') || 'Nobody';
}

/** What's wrong with a message before sending, or null. */
export function validate(title: string, body: string, a: BroadcastAudience): string | null {
  if (!title.trim()) return 'Give the message a title';
  if (title.trim().length > TITLE_MAX) return `Keep the title under ${TITLE_MAX} characters`;
  if (body.trim().length > BODY_MAX) return `Keep the message under ${BODY_MAX.toLocaleString()} characters`;
  if (!a.all && !a.user_ids?.length && !a.terminal_ids?.length) return 'Pick who gets the message';
  return null;
}

/**
 * The read-receipt row's id is derived from the message and the person, so two
 * phones opening the same message write the same row (the server allows one
 * per person per message).
 */
export async function readId(broadcastId: string, userId: string): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`read:${broadcastId}:${userId}`)));
  h[6] = (h[6]! & 0x0f) | 0x40;
  h[8] = (h[8]! & 0x3f) | 0x80;
  const x = [...h.slice(0, 16)].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${x.slice(0, 8)}-${x.slice(8, 12)}-${x.slice(12, 16)}-${x.slice(16, 20)}-${x.slice(20, 32)}`;
}

/** "2:15 PM" today, "Mon 2:15 PM" this week, else "Oct 3". */
export function sentLabel(iso: string | null, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  const days = (now.getTime() - d.getTime()) / 86_400_000;
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (d.toDateString() === now.toDateString()) return time;
  if (days < 6) return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
}
